import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/async-state';
import { Icon } from '@/components/ui/icon';
import { AppScreen, IconAction } from '@/components/ui/scene';
import { Text } from '@/components/ui/text';
import type { VenueFollowerSummary } from '@/lib/api/apiTypes';
import { getAPIErrorRecoveryCopy } from '@/lib/api/apiErrorCopy';
import { getProfileInitials } from '@/features/profile/profileFormatting';
import { getProfileHref } from '@/lib/navigation/routes';
import { useVenueFollowers } from '@/features/venues/useVenueFollowers';
import { Stack, router, useLocalSearchParams } from 'expo-router';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react-native';
import { Pressable, ScrollView, View } from 'react-native';

export default function FollowersScreen() {
  const { username, count, venueId, venueName } = useLocalSearchParams<{
    username?: string;
    count?: string;
    venueId?: string;
    venueName?: string;
  }>();
  const venueFollowersQuery = useVenueFollowers(venueId, Boolean(venueId));
  const handleBack = () => {
    if (router.canGoBack()) {
      router.back();
      return;
    }
    router.replace('/(tabs)/profile');
  };

  if (venueId) {
    const followers = venueFollowersQuery.data?.followers ?? [];
    const latestFollowers = followers.slice(0, 3);
    const earlierFollowers = followers.slice(3);
    const parsedCount = count ? Number.parseInt(count, 10) : Number.NaN;
    const followerCount = Number.isFinite(parsedCount) ? parsedCount : followers.length;

    return (
      <AppScreen>
        <Stack.Screen options={{ title: 'Audience' }} />
        {venueFollowersQuery.isPending ? (
          <LoadingState label="Loading venue audience..." />
        ) : venueFollowersQuery.isError ? (
          <ErrorState
            title="Couldn't load your audience."
            body={getAPIErrorRecoveryCopy(venueFollowersQuery.error, 'venue', 'Please try again.')}
            actionLabel="Retry"
            onAction={() => venueFollowersQuery.refetch().catch(() => undefined)}
          />
        ) : followers.length === 0 ? (
          <View className="flex-1 p-4">
            <View className="mb-6 flex-row items-center gap-3">
              <IconAction icon={ArrowLeftIcon} onPress={handleBack} />
              <View className="flex-1">
                <Text className="text-2xl font-semibold text-foreground">Audience</Text>
                <Text variant="small" className="text-muted-foreground">
                  {venueName ?? 'Your venue'}
                </Text>
              </View>
            </View>
            <EmptyState
              title="No followers yet."
              body={`When people follow ${venueName ?? 'your venue'}, they will show up here for quick audience check-ins.`}
            />
          </View>
        ) : (
          <ScrollView contentContainerStyle={{ paddingHorizontal: 16, paddingVertical: 16 }}>
            <View className="gap-4">
              <View className="flex-row items-center gap-3">
                <IconAction icon={ArrowLeftIcon} onPress={handleBack} />
                <View className="flex-1 gap-0.5">
                  <Text className="text-2xl font-semibold text-foreground">Audience</Text>
                  <Text variant="small" className="text-muted-foreground">
                    {followerCount} followers
                  </Text>
                </View>
              </View>
              <View className="gap-2">
                <Text variant="muted">
                  Newest follows first. Open a profile to see who is keeping tabs on {venueName ?? 'this venue'}.
                </Text>
                <View className="gap-2">
                  <Text variant="small" className="uppercase tracking-[1.6px] text-muted-foreground">
                    Latest followers
                  </Text>
                  <View className="gap-3">
                    {latestFollowers.map((follower, index) => (
                      <FollowerRow
                        key={follower.user_id}
                        follower={follower}
                        badgeLabel={index === 0 ? 'Newest' : 'Recent'}
                      />
                    ))}
                  </View>
                </View>
                {earlierFollowers.length > 0 ? (
                  <View className="gap-2 pt-2">
                    <Text variant="small" className="uppercase tracking-[1.6px] text-muted-foreground">
                      Earlier
                    </Text>
                    <View className="gap-3">
                      {earlierFollowers.map((follower) => (
                        <FollowerRow key={follower.user_id} follower={follower} />
                      ))}
                    </View>
                  </View>
                ) : null}
              </View>
            </View>
          </ScrollView>
        )}
      </AppScreen>
    );
  }

  return (
    <AppScreen>
      <Stack.Screen options={{ title: 'Followers' }} />
      <View className="flex-1 p-4">
        <View className="mb-6 flex-row items-center gap-3">
          <IconAction icon={ArrowLeftIcon} onPress={handleBack} />
          <View className="flex-1">
            <Text className="text-2xl font-semibold text-foreground">Followers</Text>
            <Text variant="small" className="text-muted-foreground">
              @{username ?? 'profile'}
            </Text>
          </View>
        </View>
        <View className="flex-1 items-center justify-center px-6">
          <Text variant="h3" className="text-center">
            {count ?? '0'} followers
          </Text>
          <Text variant="muted" className="mt-3 text-center">
            @{username ?? 'profile'} follower details aren&apos;t surfaced in the app yet.
          </Text>
        </View>
      </View>
    </AppScreen>
  );
}

function FollowerRow({
  follower,
  badgeLabel,
}: {
  follower: VenueFollowerSummary;
  badgeLabel?: string;
}) {
  return (
    <Pressable
      className="flex-row items-center gap-3 rounded-[20px] border border-border bg-surface-alt px-4 py-3"
      onPress={() =>
        router.push(
          getProfileHref(follower.username, {
            displayName: follower.display_name,
            bio: '',
          })
        )
      }>
      <Avatar
        alt={follower.display_name || follower.username}
        className="size-12 rounded-full border border-border bg-primary-soft">
        {follower.avatar_url ? (
          <AvatarImage source={{ uri: follower.avatar_url }} className="h-full w-full" />
        ) : null}
        <AvatarFallback className="bg-transparent">
          <Text className="text-sm font-semibold text-foreground">
            {getProfileInitials(follower.display_name || follower.username)}
          </Text>
        </AvatarFallback>
      </Avatar>
      <View className="flex-1 gap-0.5">
        <View className="flex-row items-center gap-2">
          <Text className="text-base font-semibold text-foreground">
            {follower.display_name || follower.username}
          </Text>
          {badgeLabel ? (
            <View className="rounded-full border border-primary/20 bg-primary/10 px-2 py-0.5">
              <Text variant="small" className="text-primary">
                {badgeLabel}
              </Text>
            </View>
          ) : null}
        </View>
        <Text variant="small" className="text-muted-foreground">
          @{follower.username}
        </Text>
      </View>
      <Icon as={ArrowRightIcon} size={16} className="text-muted-foreground" />
    </Pressable>
  );
}
